import { Suspense } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useBoolean } from 'react-use';

import SuccessLogo from 'assets/playlist-success.svg';
import WebPlayerLayout from 'components/b2c/WebPlayerLayout';
import Loader from 'components/shared/Loader/Loader';
import {
  PATHS,
  PATHS_WITHOUT_PLAYER,
  PATHS_WITH_CASTING_BUTTON,
  PATHS_WITHOUT_HEADER,
  PATHS_WITH_HELP_LINK,
  MICROPHONE_SIGNUP_PATHS,
} from 'constants/pathsB2c';
import Redirect from 'pages/b2c/Redirect/Redirect';
import { lazyWithRetry } from 'services/helpers/lazyWithRetry';
import { getMainSubDirectory } from 'services/helpers/location';
import withAuthentication from 'services/hoc/b2c/withAuthentication';
import { AuthState } from 'services/hoc/b2c/withAuthentication/withAuthentication.const';
import { useUser } from 'services/hooks/useUser';

const BAM_ONLINE_SUPPORT_URL = 'https://support.bam-karaokeonline.com/';

const BamOnlineOffers = lazyWithRetry(() => import('pages/b2c/BamOnlineOffers'));
const Home = lazyWithRetry(() => import('pages/b2c/Home'));
const Client = lazyWithRetry(() => import('pages/b2c/Client'));
const WebPlayer = lazyWithRetry(() => import('pages/b2c/WebPlayer'));
const Admin = lazyWithRetry(() => import('pages/b2c/Admin'));
const Publisher = lazyWithRetry(() => import('pages/b2c/Publisher'));
const PlayerPage = lazyWithRetry(() => import('pages/b2c/Player'));
const WaitingList = lazyWithRetry(() => import('pages/b2c/WaitingList'));
const MyPlaylists = lazyWithRetry(() => import('pages/b2c/MyPlaylists'));
const MyPlaylist = lazyWithRetry(() => import('pages/b2c/MyPlaylist'));
const BamPlaylist = lazyWithRetry(() => import('pages/b2c/BamPlaylist'));
const Modal = lazyWithRetry(() => import('components/b2c/Modal'));
const Gcu = lazyWithRetry(() => import('pages/b2c/Gcu'));
const Copyright = lazyWithRetry(() => import('pages/b2c/Copyright'));
const OldFaq = lazyWithRetry(() => import('pages/b2c/Faq'));
const ForgottenPassword = lazyWithRetry(() => import('pages/b2c/ForgottenPassword'));
const Confirmation = lazyWithRetry(() => import('pages/b2c/Confirmation'));

const DeclaredRoutes = () => {
  const location = useLocation();
  const currentSubDirectory = getMainSubDirectory(location.pathname);
  const [isSignupSuccessModalOpen, setIsSignupSuccessModalOpen] = useBoolean(false);
  const isPlayerHiddenOnMobile = PATHS_WITHOUT_PLAYER.includes(currentSubDirectory);
  const hasLogoBarHeader = !PATHS_WITHOUT_HEADER.includes(currentSubDirectory);
  const hasCastingButton = PATHS_WITH_CASTING_BUTTON.includes(currentSubDirectory);
  const isHelpLinkDisplayed = PATHS_WITH_HELP_LINK.includes(currentSubDirectory);
  const user = useUser();

  const webPlayerLayoutProps = {
    isPlayerHiddenOnMobile: isPlayerHiddenOnMobile,
    hasLogoBarHeader: hasLogoBarHeader,
    hasCastingButton: hasCastingButton,
    isHelpLinkDisplayed: isHelpLinkDisplayed,
    isUserConnected: user.isConnected,
    stripeId: user.userInfos?.stripeId || null,
  };

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path={PATHS.BAM_ONLINE_OFFERS} element={<BamOnlineOffers />} />
        <Route path={PATHS.HOME} element={<Home />} />
        <Route path={PATHS.CLIENT} element={<Client />} />
        <Route path={PATHS.PLAYER} element={<PlayerPage />} />
        <Route path={PATHS.PUBLISHER} element={<Publisher />} />
        <Route path={PATHS.ADMIN} element={<Admin />} />
        <Route path={PATHS.GCU} element={<Gcu />} />
        <Route path={PATHS.COPYRIGHT} element={<Copyright />} />
        <Route path={PATHS.FORGOTTEN_PASSWORD} element={<ForgottenPassword />} />
        <Route path={PATHS.CONFIRMATION} element={<Confirmation />} />

        <Route
          path={PATHS.WEB_PLAYER}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <WebPlayer />
            </WebPlayerLayout>
          }
        />
        <Route
          path={PATHS.AUTHENTICATED_CASTING}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <WebPlayer />
            </WebPlayerLayout>
          }
        />
        <Route
          path={PATHS.BAM_PLAYLIST}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <BamPlaylist />
            </WebPlayerLayout>
          }
        />
        <Route
          path={PATHS.WAITING_LIST}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <WaitingList />
            </WebPlayerLayout>
          }
        />
        <Route
          path={PATHS.MY_PLAYLISTS}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <MyPlaylists />
            </WebPlayerLayout>
          }
        />
        <Route
          path={PATHS.MY_PLAYLIST}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <MyPlaylist />
            </WebPlayerLayout>
          }
        />
        {/* TODO: remove this route once the new FAQ page is available */}
        <Route
          path={PATHS.FAQ}
          element={
            <WebPlayerLayout {...webPlayerLayoutProps}>
              <OldFaq />
            </WebPlayerLayout>
          }
        />
        <Route path={PATHS.HELP} element={<Redirect url={BAM_ONLINE_SUPPORT_URL} />} />

        {MICROPHONE_SIGNUP_PATHS.map((path: string) => (
          <Route
            key={path}
            path={path}
            element={
              <Navigate
                to={PATHS.HOME}
                state={{ authState: AuthState.MICROPHONE_SIGN_UP }}
                replace
              />
            }
          />
        ))}

        <Route path='*' element={<Navigate to={PATHS.HOME} replace />} />
      </Routes>

      <Modal
        isOpen={isSignupSuccessModalOpen}
        onClose={() => setIsSignupSuccessModalOpen(false)}
        icon={<img src={SuccessLogo} alt='svg-icon' />}
        titleId='signup.succes-modal-title'
      />
    </Suspense>
  );
};

export default withAuthentication(DeclaredRoutes);
